'use client';

import { tv } from 'tailwind-variants';
import { faStar as regularFaStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as solidFarStar } from '@fortawesome/pro-solid-svg-icons';
import { faMessageLines } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Rating } from '@/types';
import { Icon } from '@/components/ui/icon.component';
import { formatRating } from '@/utils/formatters/format-favorites';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

const MINIMUM_RATING_THRESHOLD_FOR_DISPLAY_STARS = 5;
const MINIMUM_RATING_THRESHOLD_FOR_COMMENTS = 3;

interface SearchEntityRatingComponentProps {
  isNew?: boolean;
  rating?: Rating;
  hidesEmptyState?: boolean;
}

const starStyle = tv({
  slots: {
    star: 'text-warning-400',
  },

  variants: {
    withoutRating: {
      true: {
        star: 'text-gray-400',
      },
    },
  },
});

export const SearchEntityRatingComponent = ({
  isNew,
  rating,
  hidesEmptyState,
}: SearchEntityRatingComponentProps) => {
  const { star } = starStyle();

  const ratingCount = rating?.fiveStars.count || 0;
  const commentsCount = rating?.commentsCount ?? 0;
  const hasRating = ratingCount > 0;
  const hasMinimumRatings = ratingCount >= MINIMUM_RATING_THRESHOLD_FOR_COMMENTS;
  const hasMinimumFor5StarDisplay = ratingCount >= MINIMUM_RATING_THRESHOLD_FOR_DISPLAY_STARS;

  const showsNovelty = Boolean(isNew) && !hasRating;
  const showsAverage = !isNew && Boolean(rating) && hasMinimumRatings && hasMinimumFor5StarDisplay;
  const showsComments = hasMinimumRatings && commentsCount > 0;
  const showsEmptyState = !isNew && !hasRating && !hidesEmptyState;

  if (hidesEmptyState && !showsNovelty && !showsAverage && !showsComments) {
    return null;
  }

  return (
    <div className="flex mt-1" data-testid={DataTestIdEnum.SEARCH_CARD_ENTITY_RATING}>
      <div className="flex items-center">
        {showsNovelty && (
          <>
            <span className="flex h-5 w-5 items-center justify-center">
              <Icon icon={solidFarStar} className={star({ withoutRating: false })} />
            </span>
            <Typography variant={TypographyVariant.BODY_TEXT} className="ml-1">
              Novidade
            </Typography>
          </>
        )}

        {showsAverage && (
          <>
            <span className="flex h-5 w-5 items-center justify-center">
              <Icon icon={solidFarStar} className={star()} />
            </span>
            <Typography variant={TypographyVariant.BODY_TEXT} className="ml-1">
              {rating && rating.fiveStars.average > 0 && formatRating(rating)}
            </Typography>
          </>
        )}

        {showsEmptyState && (
          <>
            <span className="flex h-5 w-5 items-center justify-center">
              <Icon icon={regularFaStar} className={star({ withoutRating: true })} />
            </span>
            <Typography variant={TypographyVariant.BODY_TEXT} className="ml-1">
              Nenhuma avaliação
            </Typography>
          </>
        )}
      </div>

      {showsComments && (
        <div className="ml-2 flex items-center">
          <span className="flex h-5 w-5 items-center justify-center">
            <Icon icon={faMessageLines} />
          </span>
          <Typography variant={TypographyVariant.BODY_TEXT} className="ml-1">
            {commentsCount}
          </Typography>
        </div>
      )}
    </div>
  );
};
